/**
 * KeyboardShortcutsOverlay — global "?" trigger that lists available shortcuts.
 * Mounted once at app root (App.tsx) so the listener is always armed.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Keyboard } from "lucide-react";

interface ShortcutEntry {
  keys: string[];
  description: string;
  scope: string;
}

const SHORTCUTS: ShortcutEntry[] = [
  { keys: ["Cmd", "K"], description: "Otwórz wybór avatara", scope: "Generator grafik" },
  {
    keys: ["Cmd", "S"],
    description: "Zapisz aktualny prompt do biblioteki",
    scope: "Generator grafik",
  },
  { keys: ["Cmd", "Z"], description: "Cofnij ostatnią zmianę", scope: "Pipeline" },
  { keys: ["J"], description: "Następne zadanie", scope: "Pipeline" },
  { keys: ["K"], description: "Poprzednie zadanie", scope: "Pipeline" },
  { keys: ["Enter"], description: "Otwórz zaznaczone zadanie", scope: "Pipeline" },
  { keys: ["?"], description: "Pokaż listę skrótów", scope: "Globalnie" },
  {
    keys: ["Enter"],
    description: "Wygeneruj grafikę z aktualnego promptu",
    scope: "Generator grafik",
  },
  { keys: ["Esc"], description: "Zamknij modal / dropdown", scope: "Globalnie" },
];

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
};

export default function KeyboardShortcutsOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === "?" && !isTypingTarget(e.target) && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0a0c10] border-white/[0.1] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="w-4 h-4 text-[#ef3e29]" />
            Skróty klawiszowe
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          {SHORTCUTS.map((s, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3 px-2 py-1.5 rounded-md hover:bg-white/[0.03]"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white/85">{s.description}</p>
                <p className="text-[10px] text-white/35">{s.scope}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {s.keys.map((k, j) => (
                  <kbd
                    key={j}
                    className="px-1.5 py-0.5 text-[10px] font-mono bg-white/[0.06] border border-white/[0.12] rounded text-white/80"
                  >
                    {k}
                  </kbd>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-white/30 text-center pt-1 border-t border-white/[0.04]">
          Wciśnij <kbd className="px-1 text-white/50">?</kbd> w dowolnym miejscu, aby ponownie
          otworzyć
        </p>
      </DialogContent>
    </Dialog>
  );
}
